/**
 * Protected Route Components
 *
 * Provides route-level access control based on authentication and authorization.
 * Uses AuthContext to determine access rights and redirects unauthorized users.
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isITStaff, isAdmin } from '../types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Requires user to be authenticated.
 * Redirects to help page if not authenticated.
 */
export function RequireAuth({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize
  if (!isInitialized || isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    // Redirect to help page, preserving the intended destination
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to be IT staff.
 * Redirects to help page if not IT staff.
 */
export function RequireITStaff({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize
  if (!isInitialized || isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  if (!isITStaff(user)) {
    // Non-IT users are redirected to help page
    return <Navigate to="/help" replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to be an admin.
 * Redirects to ticketing if IT but not admin, help page if not IT.
 */
export function RequireAdmin({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize
  if (!isInitialized || isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  if (!isAdmin(user)) {
    // IT staff without admin see ticketing, others see help
    if (isITStaff(user)) {
      return <Navigate to="/ticketing" replace />;
    }
    return <Navigate to="/help" replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to NOT be IT staff (end user only routes).
 * Used for user-facing routes that IT staff shouldn't access through this portal.
 */
export function RequireEndUser({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isInitialized, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize
  if (!isInitialized || isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/help" state={{ from: location }} replace />;
  }

  // IT staff accessing user routes - this is actually allowed
  // They might be testing or viewing as a user would
  // So we allow access but could show a different UI mode

  return <>{children}</>;
}
